import { formatLocalDateTime } from '../utils/localDateTime';
import React, { useEffect, useState } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import type { PaddleCatalogPrice as CatalogPrice, PaddleCatalogProduct as CatalogProduct } from '../../functions/src/common/paddleCatalogCache';
import { ENTERPRISE_BASE_PRODUCT_TYPE, isSubscriptionProductType } from '../../functions/src/common/paddleCatalogMetadata';
import { groupSubscriptionProducts } from '../common/paddleProductGroups';

function formatPrice(price: CatalogPrice): string {
    return (Number(price.amount) / 100).toLocaleString(undefined, {
        style: 'currency',
        currency: price.currencyCode || 'USD',
    });
}

function billingLabel(price: CatalogPrice): string {
    if (price.interval === 'one_time')
        return 'One time';
    const frequency = price.frequency || 1;
    return frequency === 1 ? `Every ${price.interval}` : `Every ${frequency} ${price.interval}s`;
}

const CustomData: React.FC<{ values: Record<string, unknown> }> = ({ values }) => {
    const entries = Object.entries(values || {});
    if (!entries.length)
        return <span className="text-xs text-slate-400">No custom data</span>;
    return <div className="flex flex-wrap gap-1.5">
        {entries.map(([key, value]) => (
            <span key={key} className="rounded-md bg-slate-100 px-2 py-1 text-[11px] font-semibold text-slate-600">
                {key} = {String(value)}
            </span>
        ))}
    </div>;
};

const ProductCard: React.FC<{ product: CatalogProduct; primary?: boolean }> = ({ product, primary = false }) => (
    <section className={`overflow-hidden rounded-2xl bg-white shadow-sm ${primary ? 'border-2 border-blue-400 ring-4 ring-blue-50' : 'border border-slate-200'}`}>
        <div className="border-b border-slate-200 p-5">
            <div className="flex items-start justify-between gap-3">
                <div>
                    <div className="flex flex-wrap items-center gap-2">
                        <h3 className="font-extrabold text-slate-900">{product.name}</h3>
                        {primary && <span className="rounded-full bg-blue-100 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide text-blue-700">Base Fee</span>}
                    </div>
                    <p className="mt-1 break-all font-mono text-[10px] text-slate-400">{product.id}</p>
                </div>
                <span className="rounded-full bg-emerald-50 px-2.5 py-1 text-[10px] font-bold uppercase text-emerald-700">{product.status}</span>
            </div>
            {product.description && <p className="mt-3 text-sm text-slate-600">{product.description}</p>}
            {product.taxCategory && <p className="mt-2 text-xs text-slate-500">Tax category: {product.taxCategory}</p>}
            <div className="mt-3"><CustomData values={product.customData} /></div>
        </div>
        <div className="divide-y divide-slate-100">
            {product.prices.map(price => (
                <div key={price.id} className="p-5">
                    <div className="flex flex-wrap items-start justify-between gap-3">
                        <div>
                            <p className="font-bold text-slate-800">{price.name || price.description || price.id}</p>
                            {price.name && price.description && <p className="mt-0.5 text-xs text-slate-500">{price.description}</p>}
                            <p className="mt-1 break-all font-mono text-[10px] text-slate-400">{price.id}</p>
                        </div>
                        <div className="text-right">
                            <p className="font-extrabold text-slate-900">{formatPrice(price)}</p>
                            <p className="text-xs font-semibold text-slate-500">{billingLabel(price)}</p>
                        </div>
                    </div>
                    <div className="mt-3 flex flex-wrap gap-3 text-xs text-slate-500">
                        {price.trialPeriod && <span>{price.trialPeriod.frequency} {price.trialPeriod.interval} trial</span>}
                        {price.quantity && <span>Quantity {price.quantity.minimum}-{price.quantity.maximum}</span>}
                    </div>
                    <div className="mt-3"><CustomData values={price.customData} /></div>
                </div>
            ))}
            {!product.prices.length && <p className="p-5 text-sm text-slate-400">No active prices.</p>}
        </div>
    </section>
);

const PlatformProductManagement: React.FC = () => {
    const [products, setProducts] = useState<CatalogProduct[]>([]);
    const [fetchedAt, setFetchedAt] = useState('');
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [error, setError] = useState('');
    const [refreshNotice, setRefreshNotice] = useState('');
    const [nextRetryAt, setNextRetryAt] = useState('');

    const loadProducts = async (action: 'load' | 'refresh') => {
        const callable = httpsCallable(getFunctions(undefined, 'us-central1'), 'managePaddleProducts', { timeout: 250000 });
        const response = await callable({ action });
        const data = response.data as { products?: CatalogProduct[]; fetchedAt?: string; refreshNotice?: string; nextRetryAt?: string };
        setProducts(Array.isArray(data.products) ? data.products : []);
        setFetchedAt(data.fetchedAt || '');
        setRefreshNotice(data.refreshNotice || '');
        setNextRetryAt(data.nextRetryAt || '');
    };

    useEffect(() => {
        const delay = Date.parse(nextRetryAt) - Date.now();
        if (!(delay > 0)) return;
        const timer = window.setTimeout(() => setNextRetryAt(''), delay);
        return () => window.clearTimeout(timer);
    }, [nextRetryAt]);

    useEffect(() => {
        let active = true;
        let retryTimer: number | undefined;
        const load = async () => {
            try {
                await loadProducts('load');
                if (active)
                    setError('');
            } catch (failure: any) {
                if (!active)
                    return;
                const message = failure.message || 'Paddle products failed loading.';
                const retrySeconds = Number(message.match(/retry in (\d+) seconds?/i)?.[1]);
                if (retrySeconds > 0) {
                    setError(`${message} Retrying automatically.`);
                    retryTimer = window.setTimeout(() => {
                        if (!active)
                            return;
                        setLoading(true);
                        void load();
                    }, retrySeconds * 1000);
                } else {
                    setError(message);
                }
            } finally {
                if (active)
                    setLoading(false);
            }
        };
        void load();
        return () => {
            active = false;
            if (retryTimer)
                window.clearTimeout(retryTimer);
        };
    }, []);

    const refresh = async () => {
        setRefreshing(true);
        setError('');
        try {
            await loadProducts('refresh');
        } catch (failure: any) {
            setError(failure.message || 'Paddle products failed refreshing.');
        } finally {
            setRefreshing(false);
        }
    };

    if (loading)
        return <div className="py-20 text-center text-sm font-semibold text-slate-400">Loading cached Paddle products...</div>;

    const subscriptionGroups = groupSubscriptionProducts(products);
    const hasSubscriptionProducts = products.some(product => isSubscriptionProductType(product.customData.type));
    const services = products.filter(product => String(product.customData?.type || '') === 'service');

    return <div className="animate-fade-in space-y-5">
        <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
                <h2 className="text-xl font-bold text-slate-800">Paddle Products</h2>
                <p className="mt-1 text-sm text-slate-500">Active subscription and service products cached from Paddle.</p>
                <p className="mt-1 text-xs text-slate-400">{fetchedAt ? `Last refreshed ${formatLocalDateTime(fetchedAt)}` : 'No cached refresh date'}</p>
            </div>
            <button type="button" disabled={refreshing || Date.parse(nextRetryAt) > Date.now()} onClick={refresh} className="rounded-lg bg-blue-600 px-5 py-2.5 text-sm font-bold text-white hover:bg-blue-700 disabled:opacity-50">
                {refreshing ? 'Refreshing...' : Date.parse(nextRetryAt) > Date.now() ? 'Paddle Cooling Down' : 'Refresh Products'}
            </button>
        </div>
        {refreshNotice && <div role="status" className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm font-semibold text-amber-900">{refreshNotice}{nextRetryAt && Date.parse(nextRetryAt) > Date.now() ? ` Retry after ${formatLocalDateTime(nextRetryAt)}.` : ''}</div>}
        {error && <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm font-semibold text-red-700">{error}</div>}
        {hasSubscriptionProducts && <section className="space-y-5">
            <div><h3 className="text-lg font-extrabold text-slate-900">Subscription Products</h3><p className="text-sm text-slate-500">Base fees and add-ons grouped by subscription.</p></div>
            {subscriptionGroups.map(group => <section key={group.type} aria-label={`${group.name} subscription products`} className="space-y-5 rounded-2xl border border-blue-100 bg-blue-50/40 p-5">
                <div><h4 className="text-lg font-extrabold text-slate-900">{group.name}</h4><p className="text-sm text-slate-500">Base fee and add-ons for this subscription.</p></div>
                <div className="grid gap-5 xl:grid-cols-2">{group.products.map(product => <div key={product.id} className={product.customData.type === ENTERPRISE_BASE_PRODUCT_TYPE ? 'xl:col-span-2' : undefined}>
                    <ProductCard product={product} primary={product.customData.type === ENTERPRISE_BASE_PRODUCT_TYPE} />
                </div>)}</div>
            </section>)}
            {!subscriptionGroups.length && <p className="rounded-xl border border-dashed border-slate-300 p-5 text-sm text-slate-500">No subscription prices are tagged. Set the subscription tag on prices in Paddle, then refresh products.</p>}
        </section>}
        {!!services.length && <section className="space-y-4">
            <div><h3 className="text-lg font-extrabold text-slate-900">Services</h3><p className="text-sm text-slate-500">One-time professional service offerings.</p></div>
            <div className="grid gap-5 xl:grid-cols-2">{services.map(product => <ProductCard key={product.id} product={product} />)}</div>
        </section>}
        {!products.length && !error && <p className="rounded-xl border border-dashed border-slate-300 p-10 text-center text-sm text-slate-400">No active subscription or service products found.</p>}
    </div>;
};

export default PlatformProductManagement;
